import React from 'react';
import { useSound } from '../sounds';

export interface NumericKeypadProps {
  value: string;
  includeOperators?: boolean;
  includeDecimal?: boolean;
  onChange: (value: string) => void;
}

/**
 * Numeric keypad widget - pure input widget with no drag-drop
 * Provides buttons for digits 0-9, optional operators (+, -, ×, ÷), and backspace
 * This is just a helper widget for answering, not part of DnD system
 */
export const NumericKeypad: React.FC<NumericKeypadProps> = ({
  value,
  includeOperators = false,
  includeDecimal = false,
  onChange,
}) => {
  const playSound = useSound();
  const handleDigit = (digit: string) => {
    playSound('keypad-press');
    onChange(value + digit);
  };

  const handleBackspace = () => {
    playSound('keypad-press');
    onChange(value.slice(0, -1));
  };

  return (
    <div className={`numeric-keypad ${includeOperators ? 'numeric-keypad-with-operators' : ''}`}>
      {/* Row 1: 1, 2, 3, + */}
      <button className="keypad-btn keypad-btn-digit" onClick={() => handleDigit('1')}>1</button>
      <button className="keypad-btn keypad-btn-digit" onClick={() => handleDigit('2')}>2</button>
      <button className="keypad-btn keypad-btn-digit" onClick={() => handleDigit('3')}>3</button>
      {includeOperators && (
        <button className="keypad-btn keypad-btn-operator" onClick={() => handleDigit('+')}>+</button>
      )}
      
      {/* Row 2: 4, 5, 6, - */}
      <button className="keypad-btn keypad-btn-digit" onClick={() => handleDigit('4')}>4</button>
      <button className="keypad-btn keypad-btn-digit" onClick={() => handleDigit('5')}>5</button>
      <button className="keypad-btn keypad-btn-digit" onClick={() => handleDigit('6')}>6</button>
      {includeOperators && (
        <button className="keypad-btn keypad-btn-operator" onClick={() => handleDigit('-')}>-</button>
      )}
      
      {/* Row 3: 7, 8, 9, × */}
      <button className="keypad-btn keypad-btn-digit" onClick={() => handleDigit('7')}>7</button>
      <button className="keypad-btn keypad-btn-digit" onClick={() => handleDigit('8')}>8</button>
      <button className="keypad-btn keypad-btn-digit" onClick={() => handleDigit('9')}>9</button>
      {includeOperators && (
        <button className="keypad-btn keypad-btn-operator" onClick={() => handleDigit('×')}>×</button>
      )}
      
      {/* Row 4: decimal/empty, 0, backspace, ÷ */}
      {includeDecimal ? (
        <button className="keypad-btn keypad-btn-decimal" onClick={() => handleDigit(',')}>,</button>
      ) : (
        <div />
      )}
      <button className="keypad-btn keypad-btn-digit" onClick={() => handleDigit('0')}>0</button>
      <button className="keypad-btn keypad-btn-backspace" onClick={handleBackspace}>⌫</button>
      {includeOperators && (
        <button className="keypad-btn keypad-btn-operator" onClick={() => handleDigit('÷')}>÷</button>
      )}
    </div>
  );
};